/**
 * CloudinaryImagePicker — admin-only modal that shows every image in a
 * Cloudinary folder as a grid of thumbnails.  Clicking one calls
 * `onPick(secureUrl)` with the FULL Cloudinary URL (so frontend reads
 * stay zero-config — no public-id juggling).
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <CloudinaryImagePicker
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     defaultFolder="Sri Durga Foods/Pickles"
 *     onPick={(url) => setForm({ ...form, image_url: url })}
 *   />
 *
 * Optional: folders list is fetched once and cached in module scope so
 * opening the picker repeatedly never re-hits the API.
 */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { imageUrl } from "../../lib/cloudinary";
import { X, Search, Folder } from "lucide-react";

let _foldersCache = null;

export default function CloudinaryImagePicker({ open, onClose, onPick, defaultFolder = "" }) {
  const [folders, setFolders] = useState(_foldersCache || []);
  const [folder, setFolder] = useState(defaultFolder);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [error, setError] = useState("");

  // Load folder list once.
  useEffect(() => {
    if (!open) return;
    if (_foldersCache) { setFolders(_foldersCache); return; }
    api.get("/admin/cloudinary/folders")
      .then((r) => {
        _foldersCache = r.data.folders || [];
        setFolders(_foldersCache);
        if (!folder && _foldersCache.length) setFolder(defaultFolder || _foldersCache[0]);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load folders"));
    // Intentionally only depend on `open`: `defaultFolder`/`folder` are read
    // for their first-render values only — the sibling effect below re-syncs
    // `folder` on every reopen. Adding those deps would trigger redundant
    // folder fetches on every prop change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // iter40 fix: reset to defaultFolder every time the picker is reopened so
  // that opening from a "Pickles" product card after a "Hero Carousel" pick
  // doesn't reuse the previous folder.
  useEffect(() => {
    if (open && defaultFolder) setFolder(defaultFolder);
  }, [open, defaultFolder]);

  // Load images for the currently-selected folder.
  useEffect(() => {
    if (!open || !folder) return;
    setLoading(true);
    setError("");
    setImages([]);
    api.get("/admin/cloudinary/images", { params: { folder } })
      .then((r) => setImages(r.data.images || []))
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load images"))
      .finally(() => setLoading(false));
  }, [open, folder]);

  if (!open) return null;

  const filtered = filter
    ? images.filter((im) => (im.filename || im.public_id || "").toLowerCase().includes(filter.toLowerCase()))
    : images;

  return (
    <div className="fixed inset-0 z-50 bg-brand-dark/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="cloudinary-picker">
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[88vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-brand-line">
          <div>
            <h3 className="font-serif text-xl text-brand-dark">Choose image from Cloudinary</h3>
            <p className="text-xs text-brand-muted mt-0.5">Pick from any folder under &ldquo;Sri Durga Foods&rdquo;</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-brand-subtle flex items-center justify-center" data-testid="cloudinary-picker-close">
            <X className="w-4 h-4" strokeWidth={1.7} />
          </button>
        </div>

        {/* Controls */}
        <div className="px-5 py-3 border-b border-brand-line flex flex-wrap gap-3 items-center bg-brand-cream/40">
          <div className="flex items-center gap-2 min-w-0">
            <Folder className="w-4 h-4 text-brand-muted" strokeWidth={1.6} />
            <select
              value={folder}
              onChange={(e) => setFolder(e.target.value)}
              className="text-sm px-3 py-1.5 rounded-lg border border-brand-line bg-white focus:outline-none focus:border-brand-chili min-w-[260px]"
              data-testid="cloudinary-picker-folder"
            >
              {folders.map((f) => <option key={f} value={f}>{f.replace("Sri Durga Foods/", "")}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2 flex-1 min-w-[180px]">
            <Search className="w-4 h-4 text-brand-muted" strokeWidth={1.6} />
            <input
              type="text"
              placeholder="Filter by filename"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="text-sm px-3 py-1.5 rounded-lg border border-brand-line bg-white w-full focus:outline-none focus:border-brand-chili"
              data-testid="cloudinary-picker-filter"
            />
          </div>
          <span className="text-xs text-brand-muted">{filtered.length} of {images.length}</span>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="text-sm text-brand-chili mb-3" data-testid="cloudinary-picker-error">{error}</div>
          )}
          {loading ? (
            <div className="text-center py-12 text-brand-muted text-sm" data-testid="cloudinary-picker-loading">Loading images…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-brand-muted text-sm">No images in this folder.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="cloudinary-picker-grid">
              {filtered.map((im) => (
                <button
                  key={im.public_id}
                  onClick={() => { onPick(im.secure_url); onClose(); }}
                  className="group relative aspect-square overflow-hidden rounded-lg bg-brand-cream border border-brand-line hover:border-brand-chili hover:shadow-md transition-all"
                  data-testid={`cloudinary-picker-item-${im.public_id}`}
                  title={im.filename}
                >
                  <img
                    src={imageUrl(im.secure_url, { width: 120, height: 120, crop: "fill" })}
                    alt={im.filename}
                    loading="lazy"
                    draggable={false}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-brand-dark/85 text-white text-[10px] px-2 py-1 truncate opacity-0 group-hover:opacity-100 transition">
                    {im.filename}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
